import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatINR, formatDate } from "@/lib/api";
import { STATUS_HINTS, STATUS_COLORS } from "@/lib/constants";
import { useRates } from "@/context/RatesContext";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { StatusTimeline } from "@/components/StatusTimeline";
import { FileUploader, DocumentList } from "@/components/FileUploader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Loader2, CalendarCheck, FileText, Award, Download, Landmark } from "lucide-react";

const downloadSanctionLetter = async (appId) => {
  try {
    const res = await api.get(`/applications/${appId}/sanction-letter`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Falcon-Sanction-Letter-${appId.replace("app_", "").toUpperCase()}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Sanction letter downloaded");
  } catch (e) {
    toast.error("Could not download sanction letter");
  }
};

const ApplicationCard = ({ app, onRefresh }) => {
  const [showUpload, setShowUpload] = useState(false);
  const { rates: RATES } = useRates();
  return (
    <article className="bg-white text-[#0F172A] rounded-2xl p-6 sm:p-8 shadow-xl shadow-black/30 rise" data-testid={`client-application-${app.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{app.loan_type} · Ref {app.id.replace("app_", "").toUpperCase()}</p>
          <h3 className="mt-1 font-display text-2xl font-semibold" data-testid="application-amount">{formatINR(app.requested_amount)}</h3>
          <p className="text-sm text-slate-500">Applied {formatDate(app.created_at)}{app.tenure_years ? ` · ${app.tenure_years} yr tenure` : ""} · {(app.interest_rate ?? RATES[app.loan_type]).toFixed(2)}% p.a.</p>
          <p className="mt-1 text-sm flex items-center gap-1.5 text-slate-700" data-testid="application-lender">
            <Landmark className="h-3.5 w-3.5 text-[#FF6B00]" /> {app.lender_name ? <>Partner lender: <strong>{app.lender_name}</strong></> : "Partner lender being matched by Falcon"}
          </p>
        </div>
        <Badge className="text-white border-0 px-3 py-1" style={{ background: STATUS_COLORS[app.status] }} data-testid="application-status-badge">{app.status}</Badge>
      </div>

      <div className="mt-8"><StatusTimeline status={app.status} /></div>

      <div className="mt-6 rounded-xl bg-[#FFF3E8] border border-orange-200 p-4" data-testid="application-status-message">
        <p className="text-sm font-semibold text-[#0F172A]">Your file is currently at the <span className="text-[#E05E00]">{app.status}</span> stage.</p>
        <p className="mt-1 text-sm text-slate-600">{STATUS_HINTS[app.status]}</p>
      </div>

      {(app.status === "Sanctioned" || app.status === "Disbursed") && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="sanction-letter-panel">
          <p className="text-sm text-emerald-800 flex items-center gap-2"><Award className="h-4 w-4" /> Your in-principle sanction communication{app.lender_name ? ` from ${app.lender_name}` : ""} is ready.</p>
          <Button size="sm" className="gold-btn rounded-full" onClick={() => downloadSanctionLetter(app.id)} data-testid="download-sanction-letter-button">
            <Download className="h-4 w-4 mr-2" /> Download PDF
          </Button>
        </div>
      )}

      {app.vip_visit_date && (
        <p className="mt-4 flex items-center gap-2 text-sm text-slate-700" data-testid="vip-visit-info">
          <CalendarCheck className="h-4 w-4 text-[#FF6B00]" /> Doorstep documentation visit scheduled for <strong>{formatDate(app.vip_visit_date)}{app.vip_visit_slot ? ` at ${app.vip_visit_slot}` : ""}</strong>
        </p>
      )}

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-slate-500" /> Documents ({app.documents.length})</h4>
          <Button variant="outline" size="sm" onClick={() => setShowUpload(!showUpload)} data-testid="toggle-upload-button">
            {showUpload ? "Close" : "Upload more"}
          </Button>
        </div>
        {app.documents.length > 0 && <div className="mt-3"><DocumentList documents={app.documents} /></div>}
        {showUpload && <div className="mt-4"><FileUploader applicationId={app.id} onUploaded={onRefresh} /></div>}
      </div>
    </article>
  );
};

export default function ClientDashboard() {
  const { user } = useAuth();
  const [apps, setApps] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/applications");
      setApps(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setApps([]);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#0B132B] text-white">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-12 py-10 lg:py-16" data-testid="client-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#FF6B00]">Client Portal</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold">Good day, {user?.name?.split(" ")[0]}.</h1>
            <p className="mt-2 text-slate-400">Your relationship desk is tracking {apps?.length ?? "…"} application{apps?.length === 1 ? "" : "s"}.</p>
          </div>
          <Button asChild className="gold-btn rounded-full h-11 px-6">
            <Link to="/apply" data-testid="new-application-button"><Plus className="h-4 w-4 mr-2" /> New application</Link>
          </Button>
        </div>

        <div className="mt-10 space-y-8">
          {apps === null ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 text-[#FF6B00] animate-spin" /></div>
          ) : apps.length === 0 ? (
            <div className="navy-glass rounded-2xl p-12 text-center" data-testid="empty-applications">
              <h3 className="font-display text-xl font-semibold">No applications yet</h3>
              <p className="mt-2 text-slate-400">Begin your first application — it takes under five minutes.</p>
              <Button asChild className="gold-btn rounded-full mt-6"><Link to="/apply" data-testid="empty-apply-button">Start application</Link></Button>
            </div>
          ) : (
            apps.map((a) => <ApplicationCard key={a.id} app={a} onRefresh={load} />)
          )}
        </div>
      </main>
    </div>
  );
}
